import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DatabaseInstanceType, OutputEc2, OutputRDS } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Container, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { getId } from '../../../../common/arn';
import { HelpPanelTopicOptions } from '../../../../common/Constants';
import { constants } from '../../../../common/Localization';
import AWSCATTable from '../../../../common/table/Table-index';
import { ColumnDefinition } from '../../../../common/table/types';
import { MatchParam } from '../../../../common/types';
import { HelpPanelContext } from '../../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import PreviewBanner from '../../../common/PreviewBanner';
import { ErrorMsgFormat } from '../../error-handler-context/CommonTcoErrorHandler';
import { generateRdsErrorMsgs } from '../../error-handler-context/helper';
import GenerateErrorMessageBox from '../../error-handler-context/TcoErrorHandler';
import { recommendationSummaryTableColumns } from './rds-helper';
import RDSInstances from './RDSInstances';

type Props = FlashContextInterface;
const tcoService = new TcoService();

function RDSRecommendation({ flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const portfolioId = match?.params?.portfolioId;
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? null;
    const isMounted = useIsMounted();

    const [ loading, setLoading ] = useState(false);
    const [ rdsAssessment, setRdsAssessment ] = useState<OutputRDS>();
    const [ ec2Output, setEC2Output ] = useState<OutputEc2>();
    const [ summaryTableItems, setSummaryTableItems ] = useState<DatabaseInstanceType[]>([]);
    const [ summaryTableColumns, setSummaryTableColumns ] = useState<ColumnDefinition<DatabaseInstanceType>[]>([]);
    const [ rdsAssessmentId, setRdsAssessmentId ] = useState('');
    const [ errorMessages, setErrorMessages ] = useState<ErrorMsgFormat[]>([]);

    const { assessment } = useContext(TcoContext);
    const { panelHandler } = useContext(HelpPanelContext);

    const history = useHistory();

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.RDS_RECOMMENDATIONS);
    }, [ panelHandler ]);

    const loadRDSAssessment = async () => {
        if (!isMounted()) {
            return;
        }

        if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) { 
            try {
                setLoading(true);
                const rdsAssessment = await tcoService.getAwsRDS(tcoAssessmentId);
                const ec2Assessment = await tcoService.getAwsEC2(tcoAssessmentId);
                if (isMounted()) {
                    setRdsAssessment(rdsAssessment.output.aws.rds);
                    if (rdsAssessment && rdsAssessment._references) {
                        setRdsAssessmentId(getId(rdsAssessment._references, 'aws-rds-assessment') ?? '');
                    }
                    setEC2Output(ec2Assessment.output.aws.ec2);
                }
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, constants.ERROR_LOADING_RDS_RECOMMENDATIONS, '');
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        }
    };

    useEffect(() => {
        loadRDSAssessment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const buildSummaryTable = () => {
        if (!rdsAssessment) {
            return;
        }
        setSummaryTableItems(rdsAssessment.instanceTypes ?? []);
        setSummaryTableColumns(recommendationSummaryTableColumns);
    };

    useEffect(() => {
        buildSummaryTable();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rdsAssessment]);

    useEffect(() => {
        if (!rdsAssessment || !portfolioId || !ec2Output) {
            return;
        }
        setErrorMessages(generateRdsErrorMsgs(rdsAssessment, ec2Output, portfolioId, tcoAssessmentId));
    }, [ rdsAssessment, portfolioId, tcoAssessmentId, ec2Output ]);

    const summaryTable = (
        <AWSCATTable
            resourceName={constants.AWS_DB_RECOMMENDATIONS}
            items={summaryTableItems}
            columnDefinitions={summaryTableColumns}
            autoGenerateVisibleContent
            enableAutoSorting
            resizeable={true}
            useDefaultDownload
        />
    );

    const detailsTable = rdsAssessment ? (
        <RDSInstances
            outputRDS={rdsAssessment}
            rdsAssessmentId={rdsAssessmentId}
        />
    ) : null;

    return (
        <Container data-testid='container'>
            <PreviewBanner customText={constants.RDS_PREVIEW_TEXT} />
            { loading ? <Spinner size='large' /> : (
                <SpaceBetween size='xl'>
                    <GenerateErrorMessageBox
                        errorMessages={errorMessages}
                        history={history}
                    />
                    {summaryTable}
                    {detailsTable}
                </SpaceBetween>
            )
            }
        </Container>
    );
}

export default withFlashContext(RDSRecommendation);
